/* ===========================================================
   Conteúdo do BLOG (spokes de intenção informacional/dor). Dados puros;
   render em screens/conteudo/blog.tsx. Cada post é /blog/<slug> indexável.

   ⚠ Slugs precisam casar com app/routes.tsx e scripts/rotas-publicas.mjs.
   Datas em ISO; o sitemap usa `atualizadoEm` como lastmod.
   =========================================================== */
import type { SecaoConteudo } from "./glossario";
import { tempoLeituraSecoes } from "./glossario";

export interface Artigo {
  slug: string;
  titulo: string;
  /** Categoria editorial (chip + capa do blog): "ROAS", "Lucro", "Métricas". */
  categoria: string;
  /** <title> de SEO. */
  tituloSeo: string;
  /** Meta description + linha de apoio (dek). */
  resumo: string;
  publicadoEm: string; // ISO "2026-06-26"
  atualizadoEm: string; // ISO
  secoes: ReadonlyArray<SecaoConteudo>;
  /** Slugs de verbetes do glossário a linkar como leitura relacionada. */
  verbetes: ReadonlyArray<string>;
}

export const ARTIGOS: ReadonlyArray<Artigo> = [
  {
    slug: "roas-bom-mas-no-prejuizo",
    titulo: "ROAS bom, mas no prejuízo: como uma campanha vende e perde dinheiro",
    categoria: "ROAS",
    tituloSeo: "ROAS bom mas no prejuízo: por que acontece e como descobrir",
    resumo:
      "Sua campanha tem ROAS alto e mesmo assim o caixa não sobra? Entenda por que o ROAS do gerenciador engana e como achar o ponto em que o anúncio começa a queimar margem.",
    publicadoEm: "2026-06-26",
    atualizadoEm: "2026-06-26",
    secoes: [
      {
        h: "O paradoxo do ROAS alto e caixa baixo",
        p: [
          "Você abre o gerenciador da Meta, vê ROAS 4,5 e respira aliviado. No fim do mês, o caixa não acompanha o faturamento. Não é azar nem fraude de atribuição — é matemática de custo que o ROAS, por desenho, ignora.",
          'O ROAS responde "quanto a campanha vendeu por real investido". Ele não responde "quanto sobrou". E é o quanto sobra que paga as contas.',
        ],
      },
      {
        h: "Por que o ROAS engana",
        p: [
          "O ROAS é receita atribuída ÷ gasto com mídia. Repare no que <em>não</em> está nessa conta: o CMV (custo do produto), o frete, o imposto. Some tudo isso e o que parecia lucro vira margem fina — às vezes negativa.",
          "Pior: a receita do numerador é a <em>atribuída pela própria plataforma</em>, que usa as janelas de atribuição mais generosas. O ROAS real, contando cada pedido uma vez, costuma ser menor que o informado.",
        ],
      },
      {
        h: "O número que falta: o ROAS de equilíbrio",
        p: [
          "O ROAS de equilíbrio é o mínimo para a campanha não dar prejuízo. Ele sai da sua margem de contribuição: <strong>ROAS de equilíbrio = 1 ÷ margem de contribuição</strong>.",
          "Se a sua margem é 40%, o equilíbrio é 2,5. Aquela campanha de ROAS 4,5 informado, que na prática roda em ROAS real 2,3, está <strong>abaixo</strong> do equilíbrio — vendendo e perdendo dinheiro a cada pedido.",
          "É por isso que escalar pelo ROAS informado é tão arriscado: você acelera exatamente a campanha que mais consome caixa.",
        ],
      },
      {
        h: "Como descobrir isso na sua loja",
        p: [
          "Primeiro, calcule sua margem de contribuição e seu ROAS de equilíbrio (dá para fazer em um minuto na calculadora gratuita abaixo). Depois, compare o ROAS real de cada campanha com essa linha.",
          "O Lucraa+ faz isso automaticamente: cruza as vendas da Nuvemshop com os anúncios da Meta e do Google, desconta CMV, frete e imposto e mostra, por campanha, se ela está acima ou abaixo do equilíbrio — com uma recomendação clara por dia.",
        ],
      },
    ],
    verbetes: ["roas", "roas-de-equilibrio", "margem-de-contribuicao"],
  },
  {
    slug: "como-calcular-lucro-de-loja-virtual",
    titulo: "Como calcular o lucro real de uma loja virtual (sem se enganar)",
    categoria: "Lucro",
    tituloSeo: "Como calcular o lucro real de uma loja virtual: guia passo a passo",
    resumo:
      "Faturamento não é lucro. Veja o passo a passo para calcular o lucro real da sua loja virtual descontando CMV, frete, imposto e mídia — e pare de confundir movimento com resultado.",
    publicadoEm: "2026-06-26",
    atualizadoEm: "2026-06-26",
    secoes: [
      {
        h: "Faturamento alto não é lucro",
        p: [
          "Muita loja cresce em faturamento e encolhe em caixa. O motivo é simples: o faturamento é o topo da conta, e há várias camadas de custo entre ele e o que de fato fica para você.",
          "Calcular o lucro real é descer essas camadas em ordem, sem pular nenhuma — é o que separa decisão de chute.",
        ],
      },
      {
        h: "Passo 1 — Receita líquida",
        p: [
          "Comece pela receita de vendas e tire devoluções e descontos. Esse é o ponto de partida honesto, não o número bruto do painel.",
        ],
      },
      {
        h: "Passo 2 — Custos variáveis (CMV, frete, imposto)",
        p: [
          "Desconte o <strong>CMV</strong> (quanto você pagou pelos produtos vendidos), o <strong>frete</strong> que ficou por sua conta e os <strong>impostos</strong> sobre a venda. O que sobra é a <strong>margem de contribuição</strong> — quanto cada venda deixa antes da mídia e dos custos fixos.",
        ],
      },
      {
        h: "Passo 3 — Mídia paga",
        p: [
          "Tire o gasto com Meta Ads, Google Ads e demais canais. Aqui você chega ao <strong>lucro das vendas</strong>: o que a operação comercial gerou de caixa no período. É a camada onde campanha no vermelho aparece.",
        ],
      },
      {
        h: "Passo 4 — Custos fixos e impostos sobre o lucro",
        p: [
          "Por fim, desconte custos fixos (aluguel, salários, ferramentas) e tributos sobre o lucro (IRPJ/CSLL) para chegar ao lucro líquido contábil. Os passos 1 a 3 são os que você ajusta no dia a dia mexendo em produtos e campanhas.",
        ],
      },
      {
        h: "Fazendo isso sem planilha",
        p: [
          "Dá para montar tudo isso numa planilha — e muita gente passa o domingo nisso. O problema é manter atualizado e cruzar pedido a pedido com o anúncio que o gerou.",
          "O Lucraa+ automatiza os quatro passos para lojas Nuvemshop: conecta vendas e anúncios pelas APIs oficiais, desconta os custos e mostra o lucro real por campanha e por produto, com uma recomendação por dia.",
        ],
      },
    ],
    verbetes: ["lucro-das-vendas", "cmv", "margem-de-contribuicao"],
  },
];

export function acharArtigo(slug: string | undefined): Artigo | undefined {
  return ARTIGOS.find((a) => a.slug === slug);
}

/** Tempo de leitura em minutos (~200 palavras/min), mínimo 1. Conta o corpo
 *  sem as tags de HTML simples dos parágrafos. */
export function tempoLeituraMin(a: Artigo): number {
  return tempoLeituraSecoes(a.resumo, a.secoes);
}
